import { useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useModal } from 'palette/Modal';
import { ConfigContext } from '@common/Config';
import {
    SEARCH_LOCAL_STORAGE_ID,
    SEARCH_ACTION, GA4_EVENT,
    GA4_METHOD,
    GLOBAL_SEARCH_THEME,
} from '@common/GlobalSearch/constants';
import { useRecentSearch } from '@common/GlobalSearch/hooks/useRecentSearch';
import { useConfig } from '@common/Hooks/useConfig';
import useRouteFormatter from '@common/Hooks/useRouteFormatter';
import { addSearchTerm as addSearchTermToSRPForm } from '@common/SearchResultsPage/actions';
import { GA_SEARCH_CATEGORY, GA_SEARCH_CLEAR_ACTION } from '@common/Tracking';
import { GA_SEARCH_VIA_KEYBOARD_ACTION } from '@common/Tracking/constants/GaEventActions';
import { useTranslate } from '@common/Translations';
import { ga4event, pushGAEventToDataLayer } from '@helpers/dataLayer';
import { isFeatureActive } from '@helpers/index';
import { saveState } from '@helpers/localStorage';
import { SEARCH_RESULT } from '@routes';
import { addSearchTerm as addSearchTermToGlobalForm, toggleSearchSidebarVisibility } from '../actions';
import GlobalSearchForm from '../components/GlobalSearchForm';
import { DEPARTMENT } from '../constants/department';
import { convertToLowerCase } from '../helpers';

interface GlobalSearchFormContainerProps {
    currentDepartment?: string;
    hasCloseButton?: boolean;
    inputRef: React.RefObject<HTMLInputElement>;
    className?: string;
    isScreenTouched: boolean;
    redirectUrl: string;
    searchTerm: string;
    suggestedQuery: string;
    onSearchTermChange: (value: string) => void;
    onTouchScreen: (state: boolean) => void;
}

function GlobalSearchFormContainer({
    currentDepartment,
    hasCloseButton,
    inputRef,
    className,
    isScreenTouched,
    redirectUrl,
    searchTerm,
    suggestedQuery,
    onSearchTermChange,
    onTouchScreen,
}: GlobalSearchFormContainerProps) {
    const config = useConfig();
    const isSRPActive = isFeatureActive('PLT-63925-srp-fe-implementation', config);
    const { recentSearches } = useRecentSearch({ limit: 10 });
    const formatRoute = useRouteFormatter();
    const translate = useTranslate();
    const { setActiveModalId } = useModal();
    const dispatch = useDispatch();
    const { URLPrefix } = useContext(ConfigContext);
    const hasSearchTerm = searchTerm?.trim()?.length > 0;

    const handleSearchInputChange = (event: { currentTarget: { value: string } }) => {
        const { value } = event.currentTarget;

        dispatch(addSearchTermToGlobalForm(value));
        dispatch(addSearchTermToSRPForm(value));
        onSearchTermChange(value);
    };

    const handleClearSearchClick = () => {
        dispatch(addSearchTermToGlobalForm(''));
        dispatch(addSearchTermToSRPForm(''));
        onSearchTermChange('');
        pushGAEventToDataLayer(GA_SEARCH_CATEGORY, GA_SEARCH_CLEAR_ACTION, '');

        inputRef.current?.focus();
    };

    const handleModalClose = () => {
        setActiveModalId(null);
        dispatch(toggleSearchSidebarVisibility(false));
    };

    const handleSubmit = () => {
        const searchKey = encodeURIComponent(searchTerm);
        // @ts-ignore
        const gender = encodeURIComponent(App.Config.navGender);
        const department = encodeURIComponent(currentDepartment || DEPARTMENT.fashion);
        const hasDepartment = isFeatureActive('PLT-60950-add-department-field-to-search-url', config);
        const isRedirectToCms = isFeatureActive('PLT-81054-search-suggestions-redirect', config);
        const searchUrl = `${URLPrefix}/search?search_key=${searchKey}&gender=${gender}${hasDepartment
            ? `&department=${department}`
            : ''}`;
        const hasToRedirectToCms = isRedirectToCms && redirectUrl;
        const newSearchUrl = hasToRedirectToCms
            ? formatRoute(redirectUrl)
            : `${formatRoute(SEARCH_RESULT)}?search_key=${searchKey}`;

        saveState(SEARCH_LOCAL_STORAGE_ID.searchResults, {
            searchKey: searchTerm,
            action: SEARCH_ACTION.search,
        });

        if (hasSearchTerm) {
            pushGAEventToDataLayer(GA_SEARCH_CATEGORY, GA_SEARCH_VIA_KEYBOARD_ACTION, searchTerm);
            window.location.href = isSRPActive ? newSearchUrl : searchUrl;
        }

        ga4event(GA4_EVENT.search, {
            search_term: convertToLowerCase(searchTerm),
            search_method: GA4_METHOD.viaHardSearch,
            search_suggested_query: suggestedQuery,
        });
    };

    const handleOnFocus = () => {
        ga4event(GA4_EVENT.startSearch, {
            search_state: recentSearches.length ? 'recent_searches' : 'empty',
        });

        onTouchScreen(false);
    };

    useEffect(() => {
        inputRef.current?.focus();

        if (isFeatureActive('PLT-57268-search-improve-scrolling-effect-on-mobile', config) && isScreenTouched) {
            inputRef.current?.blur();
        }
    }, [isScreenTouched, config, inputRef]);

    return (
        <GlobalSearchForm
            className={className}
            clearButtonLabel={translate('global-search.search-input-clear-button')}
            hasCloseButton={hasCloseButton}
            inputRef={inputRef}
            placeHolder={translate('global-search.search-input-placeholder')}
            searchTerm={searchTerm}
            theme={GLOBAL_SEARCH_THEME}
            onClearSearchClick={handleClearSearchClick}
            onFocus={handleOnFocus}
            onModalCloseClick={handleModalClose}
            onSearchInputChange={handleSearchInputChange}
            onSubmit={handleSubmit}
        />
    );
}

export default GlobalSearchFormContainer;
